import { gql, useQuery } from '@apollo/client'
import * as d3 from 'd3-scale'
import * as _ from 'lodash'
import moment from 'moment'
import { useMemo } from 'react'

import { getBrandLocationGroupId } from 'pared/utils/brand'
import { toUsdString, usdStringToNumber } from 'pared/utils/number'

import { useDateFilter } from '../../../dateFilter'
import { useGroupFilter } from '../../../groupFilter'
import { useVariables } from '../../../variables'
import { IApiDataType } from '../../types'

type IDataType = Record<
  | 'trendLocationGroupMetricValuesByDateRangeNumber'
  | 'trendLocationGroupYoyMetricValues',
  {
    nodes: {
      dateIndex: number
      metricData: Record<
        string,
        {
          name: string
          unit: 'CENT' | 'COUNT'
          value: number
        }
      >
    }[]
  }
>

interface IResultType {
  id: string
  parentId: string
  dayPart: string
  valueForDateRange1InPast: string | number | null
  valueForDateRange2InPast: string | number | null
  valueForDateRange3InPast: string | number | null
  valueForDateRange4InPast: string | number | null

  yoyForDateRange1InPast: number | null
  yoyForDateRange2InPast: number | null
  yoyForDateRange3InPast: number | null
  yoyForDateRange4InPast: number | null
}

const query = gql`
  query TrendLocationGroupMetricValuesByDateRange(
    $iEndDate: Date!
    $iGroupBy: String!
    $iLocationGroupIds: [Int!]!
    $iMetricCodes: [String!]!
    $iMetricGroups: [String!]!
    $intersectedIds: [Int!]
  ) {
    trendLocationGroupMetricValuesByDateRangeNumber(
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iFilter: {
        location_group_ids: $iLocationGroupIds
        intersected_location_group_ids: $intersectedIds
        metrics: $iMetricCodes
        metric_groups: $iMetricGroups
      }
      iDateRangeNumber: 4
    ) {
      nodes {
        locationGroupId
        businessYear
        businessQuarter
        businessMonth
        businessWeek
        businessWeekOfMonth
        dateIndex
        metricData
        metricSummaryData
      }
    }

    trendLocationGroupYoyMetricValues: trendLocationGroupMetricValuesByDateRangeNumber(
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iFilter: {
        location_group_ids: $iLocationGroupIds
        intersected_location_group_ids: $intersectedIds
        metrics: $iMetricCodes
        metric_groups: $iMetricGroups
        use_yoy: true
      }
      iDateRangeNumber: 4
    ) {
      nodes {
        locationGroupId
        businessYear
        businessQuarter
        businessMonth
        businessWeek
        businessWeekOfMonth
        dateIndex
        metricData
        metricSummaryData
      }
    }
  }
`

export const fwWingstopSalesDayPartConfigs = {
  dayPart: 'string',
  valueForDateRange1InPast: 'price',
  valueForDateRange2InPast: 'price',
  valueForDateRange3InPast: 'price',
  valueForDateRange4InPast: 'price',

  yoyForDateRange1InPast: 'percent',
  yoyForDateRange2InPast: 'percent',
  yoyForDateRange3InPast: 'percent',
  yoyForDateRange4InPast: 'percent',
} as const

export const fwWingstopChecksDayPartConfigs = {
  dayPart: 'string',
  valueForDateRange1InPast: 'number',
  valueForDateRange2InPast: 'number',
  valueForDateRange3InPast: 'number',
  valueForDateRange4InPast: 'number',

  yoyForDateRange1InPast: 'percent',
  yoyForDateRange2InPast: 'percent',
  yoyForDateRange3InPast: 'percent',
  yoyForDateRange4InPast: 'percent',
} as const

export const fwWingstopCheckAvgDayPartConfigs = {
  dayPart: 'string',
  valueForDateRange1InPast: 'string',
  valueForDateRange2InPast: 'string',
  valueForDateRange3InPast: 'string',
  valueForDateRange4InPast: 'string',

  yoyForDateRange1InPast: 'percent',
  yoyForDateRange2InPast: 'percent',
  yoyForDateRange3InPast: 'percent',
  yoyForDateRange4InPast: 'percent',
} as const

function heatmapMapping(
  value: number | string | null,
  min: number,
  max: number,
) {
  if (typeof value === 'number') {
    if (value >= 0) {
      const x = d3.scaleLinear([50, 100], [0, max])
      return x.invert(value)
    } else {
      const x = d3.scaleLinear([0, 50], [min, 0])
      return x.invert(value)
    }
  }
  return null
}

const KPI = (
  metricCode: string,
  orderModeCode: string,
): {
  id: string
  parentId: string
  dayPart: string
}[] => [
  {
    id: `${metricCode}_by_day_part_${orderModeCode}lunch`,
    parentId: 'root',
    dayPart: 'Lunch (4am to 1pm)',
  },
  {
    id: `${metricCode}_by_day_part_${orderModeCode}snack`,
    parentId: 'root',
    dayPart: 'Snack (2pm to 4pm)',
  },
  {
    id: `${metricCode}_by_day_part_${orderModeCode}dinner`,
    parentId: 'root',
    dayPart: 'Dinner (5pm to 8pm)',
  },
  {
    id: `${metricCode}_by_day_part_${orderModeCode}late_night`,
    parentId: 'root',
    dayPart: 'Late Night (9pm to 3am)',
  },
]

const buildFwWingstopDayPartHook = (metricName: string, metricCode: string) => {
  const useHook = () => {
    const { endDate } = useDateFilter()
    const { groupFilter, hasGroupBy } = useGroupFilter()
    const { variables } = useVariables()
    const brandLocationGroupId = getBrandLocationGroupId()

    const yesterday = moment().startOf('day').subtract(1, 'day')
    const adjustedEndDate =
      endDate && moment(endDate, 'YYYY-MM-DD').isAfter(yesterday)
        ? yesterday.format('YYYY-MM-DD')
        : endDate

    const dateType = variables.date?.value.type
    const dateRangeStartDate = variables.date?.getInfo(0)?.dateRange.startDate
    const iGroupBy = ((dateType?: string) => {
      switch (dateType) {
        case 'week':
        case 'this_week':
          return 'last_x_weeks'

        case 'period':
          return 'last_x_periods'

        default:
          return 'last_x_days'
      }
    })(dateType)

    const { data, loading } = useQuery<IDataType>(query, {
      variables: {
        iEndDate: adjustedEndDate,
        iGroupBy,
        iLocationGroupIds: !hasGroupBy
          ? [brandLocationGroupId]
          : groupFilter?.ids,
        intersectedIds: groupFilter?.intersectedIds,
        iMetricCodes: [],
        iMetricGroups: [
          `Expo Module - ${metricName} By Day Part And Order Mode`,
        ],
      },
      skip: !adjustedEndDate || !groupFilter,
    })

    return {
      data: useMemo((): IApiDataType => {
        const currentNodeData =
          data?.trendLocationGroupMetricValuesByDateRangeNumber?.nodes
        const yoyNodeData = data?.trendLocationGroupYoyMetricValues?.nodes

        if (!currentNodeData || !yoyNodeData) return null

        const customizedData = dateRangeStartDate
          ?.clone()
          .subtract(1, 'days')
          .isSame(yesterday)
          ? currentNodeData.map((d) => ({
              ...d,
              dateIndex: d.dateIndex + 1,
            }))
          : currentNodeData
        const yoyData = dateRangeStartDate
          ?.clone()
          ?.subtract(1, 'days')
          .isSame(yesterday)
          ? yoyNodeData.map((d) => ({
              ...d,
              dateIndex: d.dateIndex + 1,
            }))
          : yoyNodeData

        const orderModeVale =
          variables.selectOptions?.farwestOrderModes?.value?.[0]?.slice(
            -1,
          )?.[0] || 'all_order_modes'
        const orderModeCode =
          orderModeVale === 'all_order_modes' ? '' : `${orderModeVale}_`
        const kpis = KPI(metricCode, orderModeCode)

        const yoyValues: { [key: string]: number[] } = {
          past1: [],
          past2: [],
          past3: [],
          past4: [],
        }

        const result = kpis.map((kpi) => {
          const values = [1, 2, 3, 4].reduce((acc, i) => {
            const act =
              customizedData.find((d) => d.dateIndex === i)?.metricData[kpi.id]
                ?.value || null
            const yoy = yoyData.find((d) => d.dateIndex === i)?.metricData[
              kpi.id
            ]?.value

            const yoyPercent = yoy ? (((act || 0) - yoy) / yoy) * 100 : null

            yoyValues[`past${i}`].push(yoyPercent || 0)

            acc[`yoyForDateRange${i}InPast`] = yoyPercent
            acc[`valueForDateRange${i}InPast`] =
              metricCode === 'check_avg'
                ? act
                  ? toUsdString(act / 100, 2)
                  : null
                : act
            return acc
          }, {} as Record<string, string | number | null>)

          return {
            ...kpi,
            ...values,
          } as IResultType
        })

        const resultWithHeatMap = result.map((r) => {
          const heatmap = [1, 2, 3, 4].reduce((acc, i) => {
            acc[`yoyForDateRange${i}InPastHeatMap`] = heatmapMapping(
              r[`yoyForDateRange${i}InPast` as keyof IResultType],
              Math.min(...yoyValues[`past${i}`]),
              Math.max(...yoyValues[`past${i}`]),
            )

            return acc
          }, {} as Record<string, number | null>)

          return {
            ...r,
            ...heatmap,
          }
        })

        const summaryMetricCode = `${metricCode}_by_day_part_${orderModeCode}all`
        const summaryValue = [1, 2, 3, 4].reduce((acc, i) => {
          const act =
            customizedData.find((d) => d.dateIndex === i)?.metricData[
              summaryMetricCode
            ]?.value || null
          const yoy = yoyData.find((d) => d.dateIndex === i)?.metricData[
            summaryMetricCode
          ]?.value

          const yoyPercent = yoy ? (((act || 0) - yoy) / yoy) * 100 : null

          yoyValues[`past${i}`].push(yoyPercent || 0)

          acc[`yoyForDateRange${i}InPast`] = yoyPercent
          acc[`valueForDateRange${i}InPast`] =
            metricCode === 'check_avg'
              ? act
                ? toUsdString(act / 100, 2)
                : null
              : act
          return acc
        }, {} as Record<string, string | number | null>)

        return [
          ...resultWithHeatMap,
          {
            id: 'summary',
            parentId: 'root',
            dayPart: 'Total',
            ...summaryValue,
          },
        ]
      }, [groupFilter, data, variables]),
      loading,
    }
  }

  return useHook
}

export const useFwWingstopDayPartSales = buildFwWingstopDayPartHook(
  'Sales',
  'sales',
)
export const useFwWingstopDayPartChecks = buildFwWingstopDayPartHook(
  'Checks',
  'checks',
)
export const useFwWingstopDayPartCheckAvg = buildFwWingstopDayPartHook(
  'Check Average',
  'check_avg',
)
